/* eslint-disable react/no-array-index-key */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable import/prefer-default-export */
import React, {
  ChangeEvent,
  ComponentProps,
  FormEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AccordionV2,
  Breadcrumbs,
  Button,
  Carousel,
  Icon,
  ImageGallery,
  PromoBadge,
  TextInput,
  Video,
} from "@components";
import classNames from "classnames";
import { Asset } from "contentful";
import { useRouter } from "next/router";
import useFeatureFlag from "src/lib/hooks/useFeatureFlag";
import { cn } from "src/utils/tailwind-merge";
import type Swiper from "swiper";

import { BreadcrumbsItem } from "../../../components/Breadcrumbs/Breadcrumbs";
import { useProductContext } from "../../context/ProductContext";
import usePriceFormatter from "../../hooks/usePriceFormatter";
import type { PremiumShippingMessageMap } from "../../queries/data/layoutData";
import { CustomField } from "../../queries/data/productData";
import { MergedProductVariant } from "../../services/catalog/types";
import { addHeapProductCarouselInteraction } from "../../services/elevar/events";
import { isTruthy } from "../../util";
import { AccordionData } from "../../util/accordions";
import { isOptionConfigMatching } from "../../util/optionMatch";
import { AddOnExperiment, AddOnExperimentWrapper } from "../AddOn";
import { ProductOptionPicker } from "../ProductOptionPicker";
import {
  getActiveGroup,
  getValueLabel,
} from "../ProductOptionPicker/util/groups";
import { filterViewports, formatMedia, isValidEmail, isVideo } from "../util";
import {
  AffirmPromo,
  BackInStockModal,
  FeatureWrapper,
  Media,
  RichText,
  SiteBanner,
  SiteImage,
} from "..";

import PremiumShippingMessage from "./components/PremiumShippingMessage";
import { ProductOptions } from "./components/ProductOptions";
import Schematics from "./components/Schematics";
import allPaths from "./configTest/configTestProductPaths";
import { MiniProductForm, TitleAndReview } from "./components";
import selectors, { analytics } from "./selectors";

interface ProductFormProps {
  /** method to call when the selected variant is changed */
  onVariantChange: (variantId: string) => void;

  /** method to call when the add to cart button is clicked */
  onAddToCart: (data: Record<string, string>) => void;

  /** breadcrumb data */
  breadcrumbs: BreadcrumbsItem[];

  /** any custom fields to be rendered on the form */
  customFields?: CustomField[];

  /** featured content/key elements to be injected below the ATC */
  children?: ReactNode;

  /** accordion data to show in the details section */
  detailAccordions: AccordionData[];

  /** how should the product images display on desktop? as a grid or a carousel */
  displayImagesAs?: "grid" | "carousel" | undefined;

  /** title for the mini form */
  miniFormTitle?: string;

  /** title for the featured items */
  featuredItemsTitle?: string | null;

  premiumShippingMessaging: PremiumShippingMessageMap;

  hasSections: boolean;

  defaultVariantB?: MergedProductVariant | null;

  addOn: AddOnExperiment;
}

type CarouselProps = ComponentProps<typeof Carousel>;

type Options = Record<string, string>;

type ValidationMap = {
  [key in CustomField["type"]]?: (value: string) => boolean;
};
type ErrorMap = {
  [key in CustomField["type"]]?: string;
};

const validationMap: ValidationMap = {
  email: isValidEmail,
};

// TODO: i18n
const errorMap: ErrorMap = {
  email: "Please enter a valid email adress",
  tel: "Please enter valid phone number",
};

function CTAContent({ variant }: { variant: MergedProductVariant }) {
  return variant.available ? (
    <>
      {/* TODO: Make locale-aware */}
      Add to Cart
    </>
  ) : (
    <span>{variant.cta ? variant.cta.unavailable : "..."}</span>
  );
}

export const customFieldsTitle = "Recipient Details";
export const productDescriptionTitle = "Product Overview";

export const handleSlideChange = (s: Swiper) => {
  const fromIndex = s.previousIndex - 1;
  const toIndex = s.activeIndex - 1;
  addHeapProductCarouselInteraction(fromIndex, toIndex);
};

/**
 * Displays product title, ratings and description as well as variant selectors
 */
export function ProductForm({
  onVariantChange,
  onAddToCart,
  children,
  detailAccordions = [],
  featuredItemsTitle,
  customFields = [],
  breadcrumbs,
  displayImagesAs = "grid",
  miniFormTitle,
  premiumShippingMessaging,
  hasSections,
  defaultVariantB,
  addOn,
}: ProductFormProps) {
  const { locale, asPath } = useRouter();
  const pathWithoutQuery = asPath.split("?")[0];
  const { value: productConfigTestFlag } = useFeatureFlag(
    "product-option-config-test"
  );
  const { product, variant } = useProductContext();
  const formatPrice = usePriceFormatter();
  const [selectedOptions, setSelectedOptions] = useState<Options>({});
  const [variantError, setVariantError] = useState<string | null>(null);
  const [bisModalOpen, setBisModalOpen] = useState(false);

  const [customFieldValues, setCustomFieldValues] = useState(
    Object.fromEntries(customFields.map((field) => [field.id, ""]))
  );
  const [customFieldErrors, setCustomFieldErrors] = useState<
    Record<string, string | undefined>
  >({});

  const addToCartLocation = useRef(analytics.mainATCLocation);
  const setAddToCartLocation = (value: string) => () => {
    addToCartLocation.current = value;
  };

  const handleVariantChange = useCallback(
    (optionName: string, optionValue: string) => {
      // build new selected options object
      const newOptions: Options = {
        ...selectedOptions,
        [optionName]: optionValue,
      };

      const newVariant = product.variants.find((v) =>
        v.selectedOptions.every((op) => newOptions[op.name] === op.value)
      );

      if (!newVariant) {
        // TODO: i18n
        setVariantError(
          "Sorry, we couldn't update your selection. Please try a different configuration."
        );
        return;
      }

      if (newVariant.id !== variant.id) {
        onVariantChange(newVariant.id);
      }
    },
    [onVariantChange, selectedOptions, product, variant]
  );

  const [desktopAccordionPanes, setDesktopAccordionPanes] = useState([
    product.options[0].name,
  ]);
  const [mobileAccordionPanes, setMobileAccordionPanes] = useState(
    product.options.map((o) => o.name)
  );

  const validateCustomFields = useCallback(
    (data) =>
      customFields.every((field) => {
        const value = data[field.id];
        if (field.required) {
          setCustomFieldErrors({
            ...customFieldErrors,
            // TODO: i18n
            [field.id]: value ? undefined : "*Required",
          });

          if (!value) {
            return false;
          }
        }

        const validationMethod = validationMap[field.type];

        if (validationMethod) {
          const isValid = validationMethod(value);
          // TODO: find a way to get this coverage back
          // istanbul ignore next
          const error = isValid ? undefined : errorMap[field.type];
          setCustomFieldErrors({
            ...customFieldErrors,
            [field.id]: error,
          });

          return isValid;
        }

        return true;
      }),
    [customFields, customFieldErrors]
  );

  const reset = useCallback(() => {
    setCustomFieldValues(
      Object.fromEntries(customFields.map((field) => [field.id, ""]))
    );
    setCustomFieldErrors({});
  }, [customFields]);

  const handleInvalidForm = () => {
    setDesktopAccordionPanes(["custom-fields"]);
    setMobileAccordionPanes((panes) => [...panes, "custom-fields"]);
  };

  const handleAddToCart = useCallback(
    (data: Record<string, string>) => (e: FormEvent) => {
      e.preventDefault();

      if (!variant.available) {
        setBisModalOpen(true);
        return;
      }

      const isValid = validateCustomFields(data);
      if (!isValid) {
        handleInvalidForm();
        return;
      }

      onAddToCart({
        ...data,
        location: addToCartLocation.current,
      });

      reset();
    },
    [variant, onAddToCart, validateCustomFields, reset]
  );

  useEffect(() => {
    // update selected options
    const newOptions: Options = {};
    variant.selectedOptions.forEach((op) => {
      newOptions[op.name] = op.value;
    });
    setSelectedOptions(newOptions);
  }, [product, variant]);

  const activeBadge = product.badges.find((badge) =>
    isOptionConfigMatching(badge.optionMatchConfigs, selectedOptions)
  );

  const { desktopCarouselProps, mobileCarouselProps, variantSelector } =
    useMemo(() => {
      const hasMultipleImages = variant.images?.length > 1;

      const getMediaElements = (
        formattedMedia: Asset[] | undefined,
        objectFit: "cover" | "contain"
      ) =>
        formattedMedia?.map((media, index) => (
          <>
            {isVideo(media) ? (
              <div
                style={{
                  aspectRatio: `${media.fields.file.details.image?.width} / ${media.fields.file.details.image?.height}`,
                }}
                key={index}
                className={classNames(
                  "w-full h-full m-auto",
                  `object-${objectFit}`
                )}
              >
                <Video
                  sources={[
                    {
                      src: media.fields.file.url,
                      type: media.fields.file.contentType,
                    },
                  ]}
                  autoPlay
                  muted
                  loop
                  objectFit={objectFit}
                  // poster={media.preview}
                />
              </div>
            ) : (
              <SiteImage
                src={media.fields.file.url}
                alt={media.fields.title}
                key={index}
                objectFit={objectFit}
              />
            )}
            {index === 0 && activeBadge && (
              <PromoBadge
                text={activeBadge.text}
                className="absolute pointer-events-none top-4 left-4"
              />
            )}
          </>
        ));

      const getSimpleImages = (
        variant: MergedProductVariant,
        objectFit: "cover" | "contain"
      ) =>
        variant.images?.map((img, index) => (
          <>
            <SiteImage
              key={img.url}
              src={img.url}
              alt={img.description || img.title}
              priority={index < 2}
              objectFit={objectFit}
            />
            {index === 0 && activeBadge && (
              <PromoBadge
                text={activeBadge.text}
                className="absolute pointer-events-none top-4 left-4"
              />
            )}
          </>
        ));

      const mobileMediaElements = getMediaElements(
        formatMedia(filterViewports(variant, "mobile"), "mobile"),
        "cover"
      );
      const desktopMediaElements = getMediaElements(
        formatMedia(filterViewports(variant, "desktop"), "desktop"),
        "cover"
      );

      const carouselImages = getSimpleImages(variant, "cover");

      const desktopCarouselProps: CarouselProps = {
        autoplay: { enabled: false },
        className: "text-charcoal items-stretch h-full",
        children: desktopMediaElements || carouselImages,
        loop: hasMultipleImages,
        navigation: hasMultipleImages,
        onSlideChange: handleSlideChange,
        pagination: {
          type: undefined,
          position: "left",
          verticalAlignment: undefined,
        },
        priority: true,
        slidesPerView: 1,
        spaceBetween: 0,
        shouldObserve: true,
      };

      const mobileCarouselProps: CarouselProps = {
        ...desktopCarouselProps,
        children: mobileMediaElements || carouselImages,
        pagination: {
          type: "bullets",
          position: "center",
          verticalAlignment: "bottom",
          useDots: true,
          overlay: true,
        },
        navigation: false,
      };

      return {
        desktopCarouselProps,
        mobileCarouselProps,
        mobileMediaElements,
        desktopMediaElements,
        carouselImages,
        variantSelector: (variant.available ? "inStock" : "unavailable") as
          | "inStock"
          | "unavailable",
      };
    }, [variant, activeBadge]);

  const richDescription = variant.richDescription || product.richDescription;

  // Reset the form when we change products
  useEffect(() => {
    setDesktopAccordionPanes([product.options[0].name]);
    setMobileAccordionPanes(product.options.map((o) => o.name));
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id, product.options]);

  const activeBanner = product.banners.find((banner) =>
    isOptionConfigMatching(banner.optionMatchConfig, selectedOptions)
  );

  const sizeOptions = product.options.find((option) => option.name === "Size");
  const currentSizeLabel = sizeOptions?.values.find(
    ({ value }) => value === selectedOptions.Size
  )?.label;

  // TODO: i18n
  const pathsToShowSize = [
    "/products/the-bed",
    "/products/the-headboard",
    "/products/headboard-cushion",
    "/products/the-pillowboard",
    "/products/cover",
    "/products/the-mattress",
  ];
  const priceLabel = `${formatPrice(variant.price)} ${
    pathsToShowSize.find((path) => asPath.includes(path))
      ? `(${currentSizeLabel})`
      : ""
  }`;

  const productWithMiniFormTitle = {
    ...product,
    title: miniFormTitle || product.title,
  };

  const premiumShippingState = variant.premiumShipping?.value ?? "Never";
  let premiumShippingKey: keyof typeof premiumShippingMessaging | null = null;

  if (premiumShippingState === "Always") {
    premiumShippingKey = "required";
  } else if (premiumShippingState === "Sometimes") {
    premiumShippingKey = "eligible";
  }

  const whiteGloveMessaging = premiumShippingKey
    ? premiumShippingMessaging[premiumShippingKey]
    : null;

  let defaultDesktopDetailAccordionId: string | undefined;
  let defaultMobileDetailAccordionId: string | undefined;

  // set desktop default
  if (richDescription) {
    defaultDesktopDetailAccordionId = productDescriptionTitle;
  } else if (children && featuredItemsTitle) {
    defaultDesktopDetailAccordionId = featuredItemsTitle;
  }

  // set mobile default
  if (children && featuredItemsTitle) {
    defaultMobileDetailAccordionId = featuredItemsTitle;
  } else {
    defaultMobileDetailAccordionId = defaultDesktopDetailAccordionId;
  }

  const sharedAccordionProps = {
    items: [
      ...product.options.map((option) => {
        const title = option.label || option.name;
        const value = selectedOptions[option.name];

        const activeGroup = getActiveGroup(value, option.groups);
        const valueLabel =
          activeGroup?.label ??
          activeGroup?.name ??
          getValueLabel(value, option);

        return {
          id: option.name,
          analyticsTitle: title,
          title: `${title.toUpperCase()}: ${valueLabel}`,
          children: (
            <ProductOptionPicker
              selectedValue={value}
              onChange={handleVariantChange}
              {...option}
            />
          ),
        };
      }),
      customFields.length > 0 && {
        id: "custom-fields",
        title: customFieldsTitle.toUpperCase(),
        children: (
          <div className="grid gap-y-2">
            {customFields.map((field, index) => {
              const errorKeys = Object.keys(customFieldErrors).filter(
                (key) => !!customFieldErrors[key]
              );
              const [firstError] = errorKeys;
              const shouldFocus = !errorKeys.length
                ? index === 0
                : firstError === field.id;

              const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
                setCustomFieldValues((values) => ({
                  ...values,
                  [field.id]: e.target.value,
                }));
                setCustomFieldErrors((errors) => ({
                  ...errors,
                  [field.id]: undefined,
                }));
              };

              return (
                <div key={field.id}>
                  <TextInput
                    id={field.id}
                    type={field.type}
                    placeholder={field.label}
                    required={field.required}
                    data-testid={selectors.reactHookForm}
                    variant="rounded"
                    name={field.id}
                    autoFocus={shouldFocus}
                    value={customFieldValues[field.id]}
                    onChange={handleChange}
                  />
                  {customFieldErrors[field.id] && (
                    <span
                      className="block mt-2 text-blush"
                      data-testid="error-block"
                    >
                      {customFieldErrors[field.id]}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ),
      },
    ].filter(isTruthy),
  };

  const formattedDetailAccordions = detailAccordions.map(
    ({ id, title, icon, ...accordion }) => ({
      id,
      title,
      icon,
      children: (
        <div className="text-sm text-slate leading-small">
          {"content" in accordion ? (
            <div className="flex flex-col gap-3">
              <RichText richTextResponse={accordion.content} />
            </div>
          ) : (
            <Schematics
              schematics={accordion.schematics}
              selectedOptions={selectedOptions}
            />
          )}
        </div>
      ),
    })
  );

  return (
    <>
      <div
        className={classNames(
          "hidden lg:block text-2xs mx-auto",
          "px-6 py-6 full:px-8 max-w-screen-king",
          displayImagesAs === "carousel" && "sticky top-28"
        )}
        data-testid={selectors.breadcrumbs}
      >
        {/* Wrapping the title in the feature wrapper so that it fires an experiment viewed event since this is a data test */}
        {defaultVariantB ? (
          <FeatureWrapper
            flag="default-product-variant"
            defaultState="control"
            stateMap={{
              control: (
                <Breadcrumbs items={breadcrumbs} title={product.title} />
              ),
              variant: (
                <Breadcrumbs items={breadcrumbs} title={product.title} />
              ),
            }}
          />
        ) : (
          <Breadcrumbs items={breadcrumbs} title={product.title} />
        )}
      </div>
      <form
        onInvalid={handleInvalidForm}
        onSubmit={handleAddToCart(customFieldValues)}
        data-testid={selectors.productForm}
        data-bis-modal-open={bisModalOpen}
        className={classNames(
          "grid items-start w-full px-6 mx-auto transition-all grid-template-product-form",
          "grid-template-product-form-v2 gap-x-10 gap-y-6 max-w-screen-king full:px-8 lg:gap-x-8",
          displayImagesAs === "grid" ? "image-grid" : "image-carousel"
        )}
      >
        {/* product image gallery */}
        <div className="sticky z-10 md:static lg:sticky -mx-6 overflow-hidden text-white transition-all select-none top-[88px] lg:top-28 grid-area-carousel lg:mx-0">
          <Media
            greaterThanOrEqual="lg"
            className={classNames(
              "relative",
              displayImagesAs !== "grid" && "max-h-[750px] aspect-[3/2]"
            )}
          >
            {displayImagesAs === "grid" ? (
              <>
                <ImageGallery variant={variant} />
                {activeBadge && (
                  <PromoBadge
                    text={activeBadge.text}
                    className="absolute pointer-events-none top-4 left-4"
                  />
                )}
              </>
            ) : (
              <Carousel {...desktopCarouselProps} />
            )}
          </Media>
          <Media lessThan="lg" className="bg-lightgray aspect-[3/2]">
            <Carousel {...mobileCarouselProps} />
          </Media>
        </div>

        {/* product form & details */}
        <div
          className={classNames(
            "flex flex-col gap-y-3 relative lg:z-10",
            displayImagesAs === "grid" && "lg:sticky lg:top-28"
          )}
        >
          {/* title & review count */}
          <TitleAndReview
            product={productWithMiniFormTitle}
            price={priceLabel}
            hidePriceOnMobile
          />

          {/* product options */}
          <div className="relative w-full mb-6 grid-area-options">
            {productConfigTestFlag && allPaths.includes(pathWithoutQuery) ? (
              <FeatureWrapper
                flag="product-option-config-test"
                defaultState="control"
                stateMap={{
                  control: (
                    <ProductOptions
                      items={sharedAccordionProps.items}
                      desktopState={[
                        desktopAccordionPanes,
                        setDesktopAccordionPanes,
                      ]}
                      mobileState={[
                        mobileAccordionPanes,
                        setMobileAccordionPanes,
                      ]}
                    />
                  ),
                  variant: (
                    <ProductOptions
                      items={sharedAccordionProps.items}
                      desktopState={[
                        desktopAccordionPanes,
                        setDesktopAccordionPanes,
                      ]}
                      mobileState={[
                        mobileAccordionPanes,
                        setMobileAccordionPanes,
                      ]}
                    />
                  ),
                }}
              />
            ) : (
              <ProductOptions
                items={sharedAccordionProps.items}
                desktopState={[desktopAccordionPanes, setDesktopAccordionPanes]}
                mobileState={[mobileAccordionPanes, setMobileAccordionPanes]}
              />
            )}

            {/* product options: missing variant error dialog */}
            {variantError && (
              <div
                data-testid={selectors.variantError}
                className="absolute inset-0 flex items-center justify-center bg-white/75"
              >
                <div className="flex items-center max-w-sm gap-2 p-4 text-sm bg-white shadow-md">
                  <p className="flex-1 leading-tight text-blush">
                    {variantError}
                  </p>
                  <button
                    data-testid={selectors.variantErrorCloseBtn}
                    className="float-right"
                    type="button"
                    title="Dismiss"
                    onClick={() => setVariantError(null)}
                  >
                    <Icon name="close" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* product form buy banner */}
          {locale === "en-US" && activeBanner && (
            <div data-testid={selectors.buyBanner}>
              <SiteBanner
                bgColor={activeBanner.bgColor}
                color={activeBanner.color}
                className="text-left !flex !items-start gap-3 px-4"
              >
                <>
                  <div className="p-0.5 scale-75 mt-[0.45rem] grid place-items-center leading-none rounded-full border border-current">
                    <Icon
                      name="checkmark"
                      size="full"
                      className="h-2.5 w-2.5"
                    />
                  </div>
                  <div className="text-left leading-relaxed my-[0.3rem]">
                    <RichText richTextResponse={activeBanner.content} />
                  </div>
                </>
              </SiteBanner>
            </div>
          )}

          {/* product form cta & messaging */}
          <div
            data-testid={selectors.ctaContent}
            className={cn(
              "grid-area-cta bg-white lg:grid md:mt-2 -mx-6 z-20 lg:mx-0",
              "px-6 lg:px-0 pt-4 lg:-mt-2 lg:border-t border-flint/25 relative",
              "before:content-[''] before:absolute before:inset-x-6 before:bg-flint/25 before:-top-px before:h-px lg:before:hidden",
              {
                "py-0": !variant.available || variantError,
              }
            )}
          >
            {/* Price */}
            {variant.available && (
              <p className="text-[18px] leading-tight">
                {formatPrice(variant.price)}
              </p>
            )}

            {/* Payment provider info */}
            <div
              data-testid={selectors.featuredFinanceProvider}
              className={classNames(
                "text-slate leading-tight md:text-2xs marker:gap-x-0.5",
                {
                  "opacity-0 pointer-events-none":
                    !variant.available || variantError,
                  // Hide financing for none US locales.
                  // This is hidden using css instead of removing from the DOM because, on locale change, there is a race condition that causes 2 Afterpays to show.
                  hidden: locale !== "en-US",
                }
              )}
            >
              <span className="block grid-area-cta-afterpay">
                <AffirmPromo amount={variant.price} v2 />
              </span>
            </div>

            {/* ATC */}
            <div className="flex flex-col mt-3 mb-4 grid-area-cta-atc">
              <Button
                className="w-full font-light uppercase"
                data-testid={selectors.ctaButton}
                type="submit"
                onClick={setAddToCartLocation(analytics.mainATCLocation)}
                disabled={!!variantError}
                variant="pine-to-dark"
              >
                <CTAContent variant={variant} />
              </Button>
            </div>

            <MiniProductForm
              title={miniFormTitle ?? product.title}
              disabled={!!variantError}
              ctaContent={<CTAContent variant={variant} />}
              onAddToCart={setAddToCartLocation(analytics.stickyATCLocation)}
            />

            {/* Overlay to prevent image gallery peek while scrolling on mobile */}
            <div className="absolute inset-x-0 h-6 bg-white top-full lg:hidden" />

            {/* Shipping & other product messaging */}
            {variant.tooltip && variant.tooltip[variantSelector].trim() && (
              <div className="flex flex-col gap-4 text-xs leading-snug lg:mt-2 text-charcoal grid-area-cta-shipping">
                {/* Variant shipping message */}
                <p className="flex gap-2">
                  <Icon
                    name="delivery-truck"
                    size="full"
                    className="w-6 h-6 shrink-0"
                  />
                  <span className="whitespace-pre-line pt-0.5">
                    {variant.tooltip[variantSelector].trim()}
                  </span>
                </p>

                {/* Finance messaging */}
                <p className="flex gap-2">
                  <Icon name="card" size="full" className="w-6 h-6 shrink-0" />
                  {/* TODO: i18n */}
                  <span className="pt-0.5">
                    Financing available with Affirm, PayPal, or Afterpay.
                  </span>
                </p>

                {/* Premium shipping message */}
                {whiteGloveMessaging && (
                  <FeatureWrapper
                    flag="premium-shipping"
                    defaultState="disabled"
                    stateMap={{
                      enabled: (
                        <PremiumShippingMessage
                          title={whiteGloveMessaging.title}
                          body={whiteGloveMessaging.body}
                        />
                      ),
                      disabled: null,
                    }}
                  />
                )}
              </div>
            )}
          </div>

          {/* detail accordion */}
          <div
            className={classNames(
              "-mx-6 px-6 bg-white z-20 lg:z-0",
              !detailAccordions.length && {
                "-mb-12 lg:mb-0": hasSections,
                "pb-14 lg:pb-0": !hasSections,
              }
            )}
          >
            <Media greaterThanOrEqual="lg">
              <AccordionV2
                name="productDetail"
                defaultID={defaultDesktopDetailAccordionId}
                showItemLines
                collapsible
                animated
                trackInteraction
                multiple
                items={[
                  richDescription && {
                    id: productDescriptionTitle,
                    title: productDescriptionTitle,
                    children: (
                      <div className="text-sm text-slate leading-small">
                        <RichText richTextResponse={richDescription} />
                      </div>
                    ),
                  },
                  children &&
                    featuredItemsTitle && {
                      id: featuredItemsTitle,
                      title: featuredItemsTitle,
                      children,
                    },
                ]
                  .filter(isTruthy)
                  .concat(formattedDetailAccordions)}
              />
            </Media>

            <Media lessThan="lg">
              <AccordionV2
                name="productDetail"
                defaultID={defaultMobileDetailAccordionId}
                showItemLines
                collapsible
                trackInteraction
                animated
                items={[
                  children &&
                    featuredItemsTitle && {
                      id: featuredItemsTitle,
                      title: featuredItemsTitle,
                      children,
                    },
                  richDescription && {
                    id: productDescriptionTitle,
                    title: productDescriptionTitle,
                    children: (
                      <div className="text-sm text-slate leading-small">
                        <RichText richTextResponse={richDescription} />
                      </div>
                    ),
                  },
                ]
                  .filter(isTruthy)
                  .concat(formattedDetailAccordions)}
              />
            </Media>
            {/* Add on items */}
            <AddOnExperimentWrapper {...addOn} />
          </div>
        </div>

        {/* modals */}
        <BackInStockModal
          productTitle={product.title}
          productVariant={variant.title}
          variantId={variant.id}
          isOpen={bisModalOpen}
          onDismiss={() => setBisModalOpen(false)}
        />
      </form>
    </>
  );
}
